import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, RefreshCw, X } from 'lucide-react';
import { aiResultsApi } from '@/lib/aiResultsApi';
import { agentsApi } from '@/lib/agentsApi';
import { renderAiOutput } from '@/components/agents/AiOutput';
import { useToast } from '@/components/ui';
import { formatLocaleDate } from '@/lib/date';

/**
 * Proactive AI briefing on the home surface (NUMBER_ONE_PLAN W3). Surfaces the
 * most recent AI signal-summary automatically — so the user lands on a
 * narrative "here's what changed this week and where to focus" without having
 * to go ask for it — and offers a one-click refresh. Reuses the existing
 * signal-summary agent (which persists to AI history) and the shared
 * beautiful-output renderer, so the briefing is formatted, cited and never
 * raw markdown.
 */
export function WeeklyBriefing({ wsId }: { wsId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [dismissed, setDismissed] = useState(false);
  const lang = (i18n.language || 'en').split('-')[0];

  const latest = useQuery({
    queryKey: ['ai-results', wsId, 'signal_summary'],
    queryFn: () =>
      aiResultsApi.list({ workspaceId: wsId, agentKind: 'signal_summary', limit: 1 }),
    enabled: !!wsId,
  });
  const result = latest.data?.items?.[0];

  const refresh = useMutation({
    mutationFn: () => agentsApi.signalSummary({ workspaceId: wsId, sinceDays: 7, lang }),
    onSuccess: () => {
      // The agent persisted a fresh result — re-fetch the latest.
      qc.invalidateQueries({ queryKey: ['ai-results', wsId, 'signal_summary'] });
      qc.invalidateQueries({ queryKey: ['ai-results', wsId] });
    },
    onError: () => toast.error(t('agents.run_failed')),
  });

  if (dismissed || latest.isLoading) return null;

  const busy = refresh.isPending;

  return (
    <div className="mb-6 rounded-2xl border border-accent-200 bg-gradient-to-br from-accent-50/50 to-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-accent-600" />
        <span className="text-[11px] uppercase tracking-[0.16em] text-accent-700 font-semibold">
          {t('briefing.title')}
        </span>
        {result && (
          <span className="text-[11px] text-ink-400">
            {formatLocaleDate(result.createdAt, i18n.language, { withTime: false })}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => refresh.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-accent-200 bg-white px-2.5 py-1 text-xs font-semibold text-accent-700 hover:bg-accent-50 disabled:opacity-60"
          >
            <RefreshCw className={'h-3.5 w-3.5 ' + (busy ? 'animate-spin' : '')} />
            {busy ? t('briefing.generating') : t('briefing.refresh')}
          </button>
          <button
            onClick={() => setDismissed(true)}
            aria-label={t('briefing.dismiss')}
            className="rounded-md p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {busy ? (
        <p className="text-sm text-ink-500">{t('briefing.generating')}</p>
      ) : result ? (
        <div className="text-sm text-ink-800">{renderAiOutput(result.output)}</div>
      ) : (
        <div>
          <p className="text-sm text-ink-600 mb-3">{t('briefing.empty_hint')}</p>
          <button
            onClick={() => refresh.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-ink-900 px-4 py-2 text-sm font-semibold text-white hover:bg-ink-800"
          >
            <Sparkles className="h-4 w-4" />
            {t('briefing.generate')}
          </button>
        </div>
      )}
    </div>
  );
}
